import { useCallback, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import {
  Container,
  Box,
  Title,
  Button,
  Paper,
  Group,
  Text,
  Badge,
  Loader,
  Stack,
  Divider,
  Checkbox,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconRefresh,
  IconFlask,
  IconCheck,
  IconX,
  IconArrowBackUp,
  IconCircleCheck,
  IconCircleDashed,
} from "@tabler/icons-react";
import {
  useHeConfirmationDetail,
  useStainsOfDay,
  useConfirmPresence,
  useUndoPresence,
  useSetControlResult,
  useUndoControlResult,
} from "../../hooks/useWorkflows";
import { ControlConfirmationModal } from "../../components/workflows";
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner";
import { useAuthStore } from "../../store/auth.store";
import type { StainOfDayGroup } from "../../services/workflows.service";
import {
  formatDateOnlyWithShortMonth,
  getLabTodayDateOnly,
  parseDateOnly,
  type DateOnlyString,
} from "../../utils/dateOnly";
import { LAB_TIME_ZONE } from "../../utils/labTime";

const CONTROL_INSTANT_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: LAB_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

function getRowKey(group: StainOfDayGroup): string {
  return `${group.poolId ?? "gcp"}::${group.stainId}`;
}

function formatControlInstantTime(value: string): string {
  const instant = new Date(value);
  return Number.isNaN(instant.getTime())
    ? "Invalid time"
    : CONTROL_INSTANT_TIME_FORMATTER.format(instant);
}

export default function ControlSlidesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backTo =
    (location.state as { from?: string } | null)?.from ?? "/distribution";
  const backLabel = backTo.startsWith("/pathologist-review")
    ? "Back to Review"
    : "Back to Distribution";
  const roleName = useAuthStore((s) => s.profile?.roleName);
  const isPathologist = roleName === "Pathologist";
  const [selectedDate, setSelectedDate] = useState<DateOnlyString | null>(null);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  const maximumDate = useMemo(() => getLabTodayDateOnly(), []);
  const dateStr = useMemo(() => selectedDate ?? undefined, [selectedDate]);
  const selectedDateLabel = useMemo(
    () => (selectedDate ? formatDateOnlyWithShortMonth(selectedDate) : "Today"),
    [selectedDate]
  );

  const {
    data: stainsOfDay,
    isLoading: stainsLoading,
    refetch: refetchStains,
  } = useStainsOfDay(dateStr);

  const { data: heConfirmation, isLoading: heConfirmationLoading } =
    useHeConfirmationDetail(dateStr);

  const confirmPresenceMutation = useConfirmPresence();
  const undoPresenceMutation = useUndoPresence();
  const setResultMutation = useSetControlResult();
  const undoResultMutation = useUndoControlResult();

  const showConfirmButton =
    heConfirmation?.isToday &&
    !heConfirmation.practices.every((p) => p.isConfirmed);

  const handleSelectedDateChange = useCallback((value: string | null) => {
    setSelectedDate(value === null ? null : parseDateOnly(value) ?? null);
  }, []);

  const handleNavigateBack = useCallback(() => {
    navigate(backTo);
  }, [backTo, navigate]);

  const handleRefresh = useCallback(() => {
    void refetchStains();
  }, [refetchStains]);

  const handleConfirmPresence = useCallback(
    async (key: string, scannedId?: string) => {
      const group = stainsOfDay?.find((item) => getRowKey(item) === key);
      if (!group) return;
      try {
        await confirmPresenceMutation.mutateAsync({
          stainId: group.stainId,
          poolId: group.poolId,
          scannedId: scannedId || undefined,
        });
        notifications.show({
          title: "Control confirmed",
          message: `${group.stainCode}${
            group.poolName !== "GCP" ? ` · ${group.poolName}` : ""
          }`,
          color: "green",
        });
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to confirm control slide",
          color: "red",
        });
      }
    },
    [confirmPresenceMutation, stainsOfDay]
  );

  const handleBarcodeScan = useCallback(
    (barcode: string) => {
      const firstPending = stainsOfDay?.find(
        (group) => group.presenceStatus === "pending"
      );
      if (firstPending) {
        void handleConfirmPresence(getRowKey(firstPending), barcode);
      }
    },
    [handleConfirmPresence, stainsOfDay]
  );

  // Whole-page barcode scan: auto-confirm the first pending stain
  useBarcodeScanner(handleBarcodeScan);

  const handleUndoPresence = async (group: StainOfDayGroup) => {
    try {
      await undoPresenceMutation.mutateAsync({
        stainId: group.stainId,
        poolId: group.poolId,
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to undo presence",
        color: "red",
      });
    }
  };

  const handleSetResult = async (
    group: StainOfDayGroup,
    result: "pass" | "fail"
  ) => {
    try {
      await setResultMutation.mutateAsync({
        stainId: group.stainId,
        poolId: group.poolId,
        result,
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to set result",
        color: "red",
      });
    }
  };

  const handleUndoResult = async (group: StainOfDayGroup) => {
    try {
      await undoResultMutation.mutateAsync({
        stainId: group.stainId,
        poolId: group.poolId,
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to undo result",
        color: "red",
      });
    }
  };

  const isMutating =
    confirmPresenceMutation.isPending ||
    undoPresenceMutation.isPending ||
    setResultMutation.isPending ||
    undoResultMutation.isPending;

  // Group stains by pool for display
  const poolGroups = stainsOfDay
    ? Array.from(
        new Map(stainsOfDay.map((g) => [g.poolId ?? "gcp", g.poolName]))
      ).map(([poolId, poolName]) => ({
        poolId: poolId === "gcp" ? null : poolId,
        poolName,
        stains: stainsOfDay.filter((g) => (g.poolId ?? "gcp") === poolId),
      }))
    : [];

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between">
            <Group gap="md">
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={handleNavigateBack}
              >
                {backLabel}
              </Button>
              <Title order={2}>
                <Group gap="xs">
                  <IconFlask size={24} />
                  Control Slides
                </Group>
              </Title>
            </Group>
            <Group gap="md">
              <DatePickerInput
                placeholder="Today"
                value={selectedDate}
                onChange={handleSelectedDateChange}
                maxDate={maximumDate}
                clearable
                size="sm"
                style={{ width: 170 }}
              />
              <Button
                variant="subtle"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRefresh}
                loading={stainsLoading}
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {/* H&E Controls */}
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="sm">
              <Text fw={500} size="sm" c="dimmed" tt="uppercase">
                H&E Controls — {selectedDateLabel}
              </Text>
              {showConfirmButton && (
                <Button size="xs" variant="light" onClick={openConfirmModal}>
                  Confirm H&E Controls
                </Button>
              )}
            </Group>

            {heConfirmationLoading ? (
              <Loader size="sm" />
            ) : !heConfirmation || heConfirmation.practices.length === 0 ? (
              <Text size="sm" c="dimmed">
                No practices require H&E confirmation.
              </Text>
            ) : (
              <Stack gap={6}>
                {heConfirmation.practices.map((practice) => (
                  <Group key={practice.id ?? "gcp"} gap="xs">
                    {practice.isConfirmed ? (
                      <IconCircleCheck
                        size={16}
                        color="var(--mantine-color-green-6)"
                      />
                    ) : (
                      <IconCircleDashed
                        size={16}
                        color="var(--mantine-color-yellow-6)"
                      />
                    )}
                    <Text
                      size="sm"
                      c={practice.isConfirmed ? "dimmed" : undefined}
                      fw={practice.isConfirmed ? undefined : 500}
                    >
                      {practice.name}
                    </Text>
                    {practice.isConfirmed && heConfirmation.confirmedBy && (
                      <Text size="xs" c="dimmed">
                        · {heConfirmation.confirmedBy}
                        {heConfirmation.confirmedAt &&
                          ` ${formatControlInstantTime(
                            heConfirmation.confirmedAt
                          )}`}
                      </Text>
                    )}
                  </Group>
                ))}
              </Stack>
            )}
          </Paper>

          {/* Stain Controls */}
          <Paper p="md" withBorder>
            <Text fw={500} size="sm" c="dimmed" tt="uppercase" mb="sm">
              Stain Controls
            </Text>

            {stainsLoading ? (
              <Group justify="center" p="xl">
                <Loader />
              </Group>
            ) : !stainsOfDay || stainsOfDay.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No non-H&E stains used today.
              </Text>
            ) : (
              <Stack gap={0}>
                {/* Column headers */}
                <Group
                  px="xs"
                  py={6}
                  style={{
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                  }}
                >
                  <Box style={{ flex: "0 0 32px" }} />
                  <Text
                    size="xs"
                    fw={600}
                    tt="uppercase"
                    c="dimmed"
                    style={{ flex: "0 0 150px" }}
                  >
                    Stain
                  </Text>
                  {poolGroups.length > 1 && (
                    <Text
                      size="xs"
                      fw={600}
                      tt="uppercase"
                      c="dimmed"
                      style={{ flex: "0 0 160px" }}
                    >
                      Pool
                    </Text>
                  )}
                  <Text
                    size="xs"
                    fw={600}
                    tt="uppercase"
                    c="dimmed"
                    style={{ flex: 1 }}
                  >
                    Confirmed by
                  </Text>
                  <Text
                    size="xs"
                    fw={600}
                    tt="uppercase"
                    c="dimmed"
                    style={{ flex: "0 0 200px" }}
                  >
                    Result
                  </Text>
                </Group>

                {poolGroups.map((poolGroup, poolIdx) => (
                  <Box key={poolGroup.poolId ?? "gcp"}>
                    {poolGroups.length > 1 && (
                      <Group
                        px="xs"
                        py={4}
                        style={{
                          backgroundColor: "var(--mantine-color-gray-0)",
                        }}
                      >
                        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                          {poolGroup.poolName}
                        </Text>
                      </Group>
                    )}

                    {poolGroup.stains.map((group, idx) => {
                      const key = getRowKey(group);
                      const isPresenceConfirmed =
                        group.presenceStatus === "confirmed";

                      return (
                        <Group
                          key={key}
                          px="xs"
                          py="sm"
                          align="center"
                          style={{
                            borderBottom:
                              idx < poolGroup.stains.length - 1 ||
                              poolIdx < poolGroups.length - 1
                                ? "1px solid var(--mantine-color-gray-2)"
                                : "none",
                            backgroundColor:
                              group.result === "fail"
                                ? "var(--mantine-color-red-0)"
                                : group.result === "pass"
                                ? "var(--mantine-color-green-0)"
                                : "transparent",
                          }}
                        >
                          {/* Clickable checkmark — blocked if result is set */}
                          <Tooltip
                            label={
                              group.result
                                ? "Remove pass/fail result before unchecking"
                                : isPresenceConfirmed
                                ? "Undo confirmation"
                                : "Confirm control slide present"
                            }
                          >
                            <Checkbox
                              checked={isPresenceConfirmed}
                              size="md"
                              readOnly
                              disabled={isMutating || !!group.result}
                              color="green"
                              onClick={() => {
                                if (group.result) return;
                                if (isPresenceConfirmed) {
                                  handleUndoPresence(group);
                                } else {
                                  handleConfirmPresence(key);
                                }
                              }}
                              style={{
                                cursor: group.result
                                  ? "not-allowed"
                                  : "pointer",
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>

                          {/* Stain */}
                          <Stack gap={0} style={{ flex: "0 0 150px" }}>
                            <Text size="sm" fw={700}>
                              {group.stainCode}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {group.stainName}
                            </Text>
                          </Stack>

                          {/* Pool (multi-pool only) */}
                          {poolGroups.length > 1 && (
                            <Box style={{ flex: "0 0 160px" }}>
                              <Badge size="sm" variant="light" color="violet">
                                {group.poolName}
                              </Badge>
                            </Box>
                          )}

                          {/* Confirmed by */}
                          <Box style={{ flex: 1 }}>
                            {isPresenceConfirmed &&
                            group.presenceConfirmedByName ? (
                              <Text size="xs" c="dimmed">
                                {group.presenceConfirmedByName}
                                {group.presenceConfirmedAt &&
                                  ` · ${formatControlInstantTime(
                                    group.presenceConfirmedAt
                                  )}`}
                                {group.scannedId && (
                                  <Text span ff="monospace" size="xs">
                                    {" "}
                                    · {group.scannedId}
                                  </Text>
                                )}
                              </Text>
                            ) : (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </Box>

                          {/* Result — pathologist only */}
                          <Box style={{ flex: "0 0 200px" }}>
                            {!isPresenceConfirmed ? (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            ) : group.result ? (
                              <Stack gap={4}>
                                <Group gap="xs" align="center">
                                  {group.result === "pass" ? (
                                    <IconCheck
                                      size={14}
                                      color="var(--mantine-color-green-6)"
                                    />
                                  ) : (
                                    <IconX
                                      size={14}
                                      color="var(--mantine-color-red-6)"
                                    />
                                  )}
                                  <Text
                                    size="sm"
                                    fw={600}
                                    c={
                                      group.result === "pass" ? "green" : "red"
                                    }
                                  >
                                    {group.result === "pass" ? "Pass" : "Fail"}
                                  </Text>
                                  {group.reviewedByName && (
                                    <Text size="xs" c="dimmed">
                                      · {group.reviewedByName}
                                      {group.reviewedAt &&
                                        ` ${formatControlInstantTime(
                                          group.reviewedAt
                                        )}`}
                                    </Text>
                                  )}
                                </Group>
                                {isPathologist && (
                                  <Group gap={4}>
                                    <Tooltip
                                      label={
                                        group.result === "pass"
                                          ? "Mark as fail"
                                          : "Mark as pass"
                                      }
                                    >
                                      <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color={
                                          group.result === "pass"
                                            ? "red"
                                            : "green"
                                        }
                                        loading={isMutating}
                                        onClick={() =>
                                          handleSetResult(
                                            group,
                                            group.result === "pass"
                                              ? "fail"
                                              : "pass"
                                          )
                                        }
                                      >
                                        {group.result === "pass" ? (
                                          <IconX size={11} />
                                        ) : (
                                          <IconCheck size={11} />
                                        )}
                                      </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label="Remove result">
                                      <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color="gray"
                                        loading={isMutating}
                                        onClick={() => handleUndoResult(group)}
                                      >
                                        <IconArrowBackUp size={11} />
                                      </ActionIcon>
                                    </Tooltip>
                                  </Group>
                                )}
                              </Stack>
                            ) : isPathologist ? (
                              <Group gap="xs">
                                <Button
                                  size="xs"
                                  color="green"
                                  variant="light"
                                  leftSection={<IconCheck size={12} />}
                                  loading={isMutating}
                                  onClick={() => handleSetResult(group, "pass")}
                                >
                                  Pass
                                </Button>
                                <Button
                                  size="xs"
                                  color="red"
                                  variant="light"
                                  leftSection={<IconX size={12} />}
                                  loading={isMutating}
                                  onClick={() => handleSetResult(group, "fail")}
                                >
                                  Fail
                                </Button>
                              </Group>
                            ) : (
                              <Text size="xs" c="dimmed">
                                Pending pathologist review
                              </Text>
                            )}
                          </Box>
                        </Group>
                      );
                    })}
                    {poolIdx < poolGroups.length - 1 && <Divider />}
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Box>

      <ControlConfirmationModal
        opened={confirmModalOpened}
        onClose={closeConfirmModal}
      />
    </Container>
  );
}
